import { ampli } from "@/ampli"
import { toast } from "@/components/Toast"
import { JobPostingPayDisplay, JobPostingPayType, type PostAJobResponseDataFragment } from "@/gql/graphql"
import {
  CreateClientJobPostingDocument,
  CreateCreativeJobPostingDocument,
  GetHirePageFilterCountsDocument,
  InviteToJobDocument,
  JobPostingFileType,
  JobPostingLocationType,
  MyJobsActiveJobPostsCreativeQueryDocument,
  MyJobsDraftJobPostsQueryDocument,
  PublishJobPostDocument,
  UpdateClientJobPostingDocument,
  UpdateCreativeJobPostingDocument,
} from "@/gql/graphql"
import { CreateJobPostingFileDocument } from "@/gql/server"
import { decodeGraphqlNodeId } from "@/tracking"
import type { NovaSession } from "@/types"
import adjustDateForTimezone from "@/utils/dates"
import { generateJobTitle } from "@/utils/generateJobTitle"
import { useApolloClient, useMutation } from "@apollo/client"
import { captureException } from "@sentry/nextjs"
import axios from "axios"
import merge from "lodash/merge"
import { useCallback, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { stringify } from "wellknown"
import { POST_A_JOB_MODAL_DEFAULT_VALUES } from "./constants"
import { CreativeInvite } from "./SuccessStep"
import { PartialPostAJobModalFormValues, PostAJobModalFormValues } from "./types"

export enum PostAJobSteps {
  StepOne = 1,
  StepTwo = 2,
  StepThree = 3,
  InviteCreatives = 4,
  Success = 5,
}

const steps = Object.values(PostAJobSteps).filter(value => typeof value === "number") as number[]
const maxSteps = steps.length

export const usePostAJobModal = (
  serverSession: NovaSession,
  onClose: () => void,
  updateData?: PartialPostAJobModalFormValues | null
) => {
  const [isMutatingInviteCreatives, setIsMutatingInviteCreatives] = useState(false)
  const [selectedCreativeIdsToInvite, setSelectedCreativeIdsToInvite] = useState<string[]>([])
  const [invitedCreatives, setInvitedCreatives] = useState<CreativeInvite[]>([])
  const [responseData, setResponseData] = useState<PostAJobResponseDataFragment | null>(null)
  const [createClientJobPost] = useMutation(CreateClientJobPostingDocument)
  const [createCreativeJobPost] = useMutation(CreateCreativeJobPostingDocument)
  const [updateCreativeJobPosting] = useMutation(UpdateCreativeJobPostingDocument)
  const [updateClientJobPosting] = useMutation(UpdateClientJobPostingDocument)
  const [publishJobPost] = useMutation(PublishJobPostDocument)
  const [createJobPostFile] = useMutation(CreateJobPostingFileDocument)
  const [inviteToJob] = useMutation(InviteToJobDocument)

  const handleInviteCreatives = async () => {
    setIsMutatingInviteCreatives(true)
    try {
      for (const creativeId of selectedCreativeIdsToInvite) {
        ampli.newInviteSuggestedCreative({
          entry_point: "JobPostingForm",
          job_id: decodeGraphqlNodeId(responseData?.id!).numericalId || "",
          status: responseData?.completionStatus?.currentStep as string,
        })
        const response = await inviteToJob({
          variables: { input: { creative: creativeId, jobPosting: responseData?.id! } },
        })

        if (response.data?.inviteToJob?.jobInvitation?.id) {
          const matchedCreative = responseData?.suggestedCreatives?.edges?.find(
            edge => edge?.node?.id === creativeId
          )?.node
          const creative = matchedCreative?.__typename === "IndividualCreative" ? matchedCreative : null

          if (creative?.avatar168 && creative?.avatarInitials) {
            setInvitedCreatives(prev => [
              ...prev,
              {
                avatarUrl: creative?.avatar168 as string,
                avatarInitials: creative?.avatarInitials,
              },
            ])
          }
        }
      }
      ampli.inviteToJobComplete({
        entry_point: "JobPostingForm",
        job_id: decodeGraphqlNodeId(responseData?.id!).numericalId || "",
        job_type:
          responseData?.__typename === "ClientJobPosting"
            ? "client"
            : responseData?.__typename === "CreativeJobPosting"
              ? "creative"
              : "",
      })
      setStep(PostAJobSteps.Success)
    } catch (error) {
      toast.error("Failed to send invitation")
      captureException(error)
    }
    setIsMutatingInviteCreatives(false)
  }

  const handleClickInviteCard = (creativeId: string) => {
    setSelectedCreativeIdsToInvite(prev => {
      const isAlreadySelected = prev.includes(creativeId)
      if (isAlreadySelected) {
        return prev.filter(id => id !== creativeId)
      } else {
        return [...prev, creativeId]
      }
    })
  }

  // These are the fields that are displayed in the first step of the modal
  // they dont represent the values selected, just the temporary display string values
  // need to store here for validation purposes
  const [stepOneDisplayFields, setStepOneDisplayFields] = useState({
    position: "",
    location: "",
    workType: "",
  })

  const onChangeStepOneDisplayField = (fieldName: string, value: string) => {
    const jobPostId = responseData?.id || updateData?.jobPostingId

    ampli.postAJobSetDetail({
      job_post_field: fieldName,
      job_id: decodeGraphqlNodeId(jobPostId).numericalId || "",
      job_post_field_value: value,
    })
    setStepOneDisplayFields(prev => ({ ...prev, [fieldName]: value }))
  }

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCreatingDraft, setIsCreatingDraft] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const methods = useForm<PostAJobModalFormValues>({
    mode: "onBlur",
    defaultValues: POST_A_JOB_MODAL_DEFAULT_VALUES,
  })

  const { watch, setValue, reset } = methods
  const startDate = watch("startDate")
  const endDate = watch("endDate")
  const locationName = watch("location.name")
  const workType = watch("workType")
  const remote = watch("remote")
  const position = watch("position")
  const payDisplay = watch("payDisplay")
  const payType = watch("payType")
  const budgetAmount = watch("budgetAmount")
  const budgetMaxAmount = watch("budgetMaxAmount")

  const userIsClient = serverSession?.account === "client"
  const [step, setStep] = useState(1)

  useEffect(() => {
    if (updateData) {
      const mergedData = merge({}, POST_A_JOB_MODAL_DEFAULT_VALUES, updateData)

      const updateDataLocationDisplay = updateData?.remote
        ? "Remote"
        : updateData?.location?.name
          ? updateData?.location?.name
          : ""

      setStepOneDisplayFields({
        position: updateData?.position?.display || "",
        location: updateDataLocationDisplay,
        workType: updateData?.workType?.display || "",
      })

      methods.reset(mergedData)
    }
  }, [updateData, methods, setStepOneDisplayFields])

  const onNext = useCallback(async () => {
    methods.clearErrors()

    // @ts-ignore
    const currentStepFields: (keyof PostAJobModalFormValues)[] = {
      1: ["position.value"],
      2: ["fee"],
      3: [],
      4: [],
      5: [],
    }[step]

    if (step === 1) {
      if (!stepOneDisplayFields.position) {
        methods.setError("position.value", {
          type: "manual",
          message: "Please select a position.",
        })
        ampli.postAJobError({
          account_type: serverSession?.account,
          job_post_field: "position",
        })
        return
      }
      if (stepOneDisplayFields.position && !position.value) {
        methods.setError("position.value", {
          type: "manual",
          message: "Please select a position from the dropdown.",
        })
        ampli.postAJobError({
          account_type: serverSession?.account,
          job_post_field: "position",
        })
        return
      }

      if (!remote && !locationName) {
        methods.setError("location.name", {
          type: "manual",
          message: "Please provide a location.",
        })
        return // Exit if validation fails
      }

      if (stepOneDisplayFields.location && !remote && !locationName) {
        methods.setError("location.name", {
          type: "manual",
          message: "Please select a location from the dropdown.",
        })
        return // Exit if validation fails
      }

      const adjustedStartDate = startDate
        ? adjustDateForTimezone(startDate?.toISOString())?.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
          })
        : null

      const adjustedEndDate = endDate
        ? adjustDateForTimezone(endDate.toISOString())?.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
          })
        : null

      const title = generateJobTitle({
        position: position.display,
        locationType: remote ? JobPostingLocationType.Telecommute : JobPostingLocationType.OnSite,
        creativeWorkType: {
          indefiniteArticle: workType.indefiniteArticle,
          name: workType.display,
        },
        locationName,
        startDate: adjustedStartDate ? new Date(adjustedStartDate) : null,
        endDate: adjustedEndDate ? new Date(adjustedEndDate) : null,
      })
      setValue("title", title)
    }

    if (step === 2) {
      if (payDisplay === JobPostingPayDisplay.Exact && !budgetAmount?.value) {
        methods.setError("budgetAmount", {
          type: "manual",
          message: "Pay is required. Add a range if flexible. ",
        })
        ampli.postAJobError({
          account_type: serverSession?.account,
          job_post_field: "pay",
        })
        return
      }

      if (payDisplay === JobPostingPayDisplay.Range && !budgetAmount?.value) {
        methods.setError("budgetAmount", {
          type: "manual",
          message: "Please provide a min pay.",
        })
        ampli.postAJobError({
          account_type: serverSession?.account,
          job_post_field: "pay",
        })
        return
      }

      if (payDisplay === JobPostingPayDisplay.Range && !budgetMaxAmount?.value) {
        methods.setError("budgetMaxAmount", {
          type: "manual",
          message: "Please provide a max pay.",
        })
        ampli.postAJobError({
          account_type: serverSession?.account,
          job_post_field: "pay",
        })
        return
      }

      if (payDisplay === JobPostingPayDisplay.Range && (budgetAmount?.value || 0) > (budgetMaxAmount?.value || 0)) {
        methods.setError("budgetAmount", {
          type: "manual",
          message: "Min pay cannot be greater than max pay.",
        })
        ampli.postAJobError({
          account_type: serverSession?.account,
          job_post_field: "pay",
        })
        return
      }
    }

    const isValid = await methods.trigger(currentStepFields)
    if (isValid && step < maxSteps) {
      setStep(prev => Math.min(prev + 1, maxSteps))
    }
  }, [
    methods,
    step,
    remote,
    locationName,
    stepOneDisplayFields.location,
    stepOneDisplayFields.position,
    position.value,
    position.display,
    startDate,
    endDate,
    workType.indefiniteArticle,
    workType.display,
    setValue,
    payDisplay,
    budgetAmount?.value,
    budgetMaxAmount?.value,
  ])

  const onBack = useCallback(() => {
    if (step > 1) {
      setStep(prev => Math.max(prev - 1, 0))
    }
  }, [step])

  const publishPost = async (jobPostingId: string) => {
    if (jobPostingId) {
      // Publish the job post
      let publishReponse
      try {
        publishReponse = await publishJobPost({
          variables: {
            input: {
              id: jobPostingId,
            },
          },
          refetchQueries: ["MyJobsActiveJobPostsCreativeQuery", "MyJobsActiveJobPostsClientQuery", "CreativeJobs"],
          awaitRefetchQueries: true,
        })

        if (publishReponse.errors) {
          console.error(publishReponse.errors)
          setIsSubmitting(false)
          captureException(publishReponse.errors)
        }
      } catch (e) {
        console.error(e)
        captureException(e)
        setIsSubmitting(false)
        return
      }
    }
  }

  const uploadImageFile = async ({
    file,
    jobPostId,
    fileName,
  }: {
    file: string
    jobPostId: string
    fileName: string
  }) => {
    let response
    try {
      response = await createJobPostFile({
        variables: {
          jobPostingId: jobPostId,
          originalFileName: fileName,
          type: JobPostingFileType.CoverImage,
        },
      })
    } catch (e) {
      console.error(e)
      return
    }

    if (response?.errors) {
      console.error(response.errors)
      return
    }

    const createdFile = response?.data?.createJobPostingFile?.createdFile
    const fields = JSON.parse(createdFile?.presignedPost.fields)

    const blobResponse = await fetch(file)
    const blob = await blobResponse.blob()
    const contentType = blob.type || "application/octet-stream"

    const formData = new FormData()

    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value as string)
    })

    formData.append("file", blob, fileName)
    formData.append("Content-Type", contentType)

    try {
      const s3Response = await axios.post(createdFile?.presignedPost.url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })

      return s3Response
    } catch (e) {
      captureException(e)
      console.error(e)
      return
    }
  }

  const upsertJobPosting = async (data: PostAJobModalFormValues) => {
    const locationData = stringify({
      type: "Point",
      coordinates: [Number(data.location.lon), Number(data.location.lat)],
    })

    const inputVariables = {
      startDate: data.startDate?.toISOString()?.split("T")[0],
      endDate: data.endDate?.toISOString()?.split("T")[0],
      position: data.position.value,
      locationName: data.location.name,
      location: data.location.lon && data.location.lat ? locationData : null,
      skills: data.skills.map(skill => skill.id),
      description: data.description,
      payType: data.payType,
      employmentType: data.jobType,
      locationType: data.remote ? JobPostingLocationType.Telecommute : JobPostingLocationType.OnSite,
      datesAreFlexible: data.datesFlexible,
      payRate: data.rateFrequency,
      creativeWorkType: data.workType.value,
      title: data.title,
      payDisplay: data.payDisplay,
      ...(data.payType === JobPostingPayType.FixedFee && data.budgetAmount?.value
        ? {
            budget: {
              currency: data.budgetCurrency,
              amount: data.budgetAmount.value,
            },
          }
        : data.payType === JobPostingPayType.RateBased && data.budgetAmount?.value
          ? {
              pay: {
                currency: data.budgetCurrency,
                amount: data.budgetAmount.value,
              },
            }
          : {}),
      ...(data.payDisplay === JobPostingPayDisplay.Range &&
      data.payType === JobPostingPayType.FixedFee &&
      data.budgetAmount?.value
        ? {
            budgetMax: {
              currency: data.budgetCurrency,
              amount: data.budgetMaxAmount.value,
            },
          }
        : data.payDisplay === JobPostingPayDisplay.Range &&
            data.payType === JobPostingPayType.RateBased &&
            data.budgetAmount?.value
          ? {
              payMax: {
                currency: data.budgetCurrency,
                amount: data.budgetMaxAmount.value,
              },
            }
          : {}),
    }

    let createResponse
    let updateResponse
    try {
      if (updateData?.jobPostingId) {
        if (userIsClient) {
          updateResponse = await updateClientJobPosting({
            variables: {
              input: {
                ...inputVariables,
                id: updateData.jobPostingId,
              },
            },
            refetchQueries: [GetHirePageFilterCountsDocument, "MyJobsDraftJobPostsQuery", "JobOwnerHeaderQuery"],
          })
        } else {
          updateResponse = await updateCreativeJobPosting({
            variables: {
              input: {
                ...inputVariables,
                id: updateData.jobPostingId,
              },
            },
          })
        }
      } else {
        if (userIsClient) {
          createResponse = await createClientJobPost({
            variables: {
              input: {
                ...inputVariables,
                client: serverSession.user.client.id!,
              },
            },
            refetchQueries: [
              GetHirePageFilterCountsDocument,
              MyJobsDraftJobPostsQueryDocument,
              MyJobsActiveJobPostsCreativeQueryDocument,
              GetHirePageFilterCountsDocument,
              "MyJobsDraftJobPostsQuery",
            ],
          })
        } else {
          createResponse = await createCreativeJobPost({
            variables: {
              input: {
                ...inputVariables,
                creative: serverSession.user.creative.id!,
              },
            },
            refetchQueries: [
              MyJobsDraftJobPostsQueryDocument,
              GetHirePageFilterCountsDocument,
              MyJobsActiveJobPostsCreativeQueryDocument,
            ],
          })
        }
      }
    } catch (e) {
      console.error(e)
      return null
    }

    const responseData = updateResponse?.data?.updateJobPosting || createResponse?.data?.createJobPosting

    if (responseData?.errors && responseData?.errors.length > 0) {
      console.error(responseData.errors)
      captureException(responseData.errors)
      if (responseData.errors[0]?.messages?.[0]) {
        toast.error(responseData.errors[0]?.messages?.[0])
      }
      return null
    }

    return responseData?.jobPosting as PostAJobResponseDataFragment
  }

  const handleReset = () => {
    setTimeout(() => {
      reset()
      setStep(1)
      // setJobPostId(undefined)
      setResponseData(null)
      setIsCreatingDraft(false)
      setInvitedCreatives([])
      setStepOneDisplayFields({
        position: "",
        location: "",
        workType: "",
      })
    }, 300)
  }

  const client = useApolloClient()

  const createAndPublishJobPost = async (data: PostAJobModalFormValues) => {
    if (isSubmitting || step !== 3 || isCreatingDraft) return
    setIsSubmitting(true)

    const responseData: PostAJobResponseDataFragment | null = await upsertJobPosting(data)

    setResponseData(responseData)

    if (!responseData?.id) {
      setIsSubmitting(false)
      toast.error("Oh no! There was an error creating this job")
      return
    } else {
      if (data.referenceImage?.file) {
        await uploadImageFile({
          file: data.referenceImage?.file,
          jobPostId: responseData.id,
          fileName: data.referenceImage?.name,
        })
      }
      await publishPost(responseData.id)
      client.cache.modify({
        id: client.cache.identify({ __typename: responseData.__typename, id: responseData.id }),
        fields: {
          coverImage: () => ({
            __typename: "JobPostingFile",
            id: "temp-image-id",
            'thumbnailUrl({"width":2000})': data.referenceImage?.file,
            thumbnailFocalPointX: 0.5,
            thumbnailFocalPointY: 0.5,
            'thumbnailUrl({"width":645})': data.referenceImage?.file,
          }),
        },
      })

      ampli.postAJobPublish({
        job_id: decodeGraphqlNodeId(responseData.id).numericalId || "",
        num_suggestions: responseData.suggestedCreatives.totalCount!,
      })

      if (responseData.suggestedCreatives.totalCount! > 0) {
        setStep(PostAJobSteps.InviteCreatives)
        ampli.inviteToJobStart({
          entry_point: "Success",
        })
      } else {
        setStep(PostAJobSteps.Success)
      }

      setIsSubmitting(false)
    }
  }

  const onDone = () => {
    if (!updateData) {
      methods.reset(POST_A_JOB_MODAL_DEFAULT_VALUES) // Resets the form state
      handleReset()
    }
  }

  const createAndDraftJobPost = async (data: PostAJobModalFormValues) => {
    if (isSubmitting || isCreatingDraft) return
    setIsCreatingDraft(true)

    const responseData: PostAJobResponseDataFragment | null = await upsertJobPosting(data)

    setResponseData(responseData)

    if (!responseData?.id) {
      setIsCreatingDraft(false)
      toast.error("Oh no! There was an error creating this job")
      return
    } else {
      if (data.referenceImage?.file) {
        await uploadImageFile({
          file: data.referenceImage?.file,
          jobPostId: responseData.id,
          fileName: data.referenceImage?.name,
        })
      }
      ampli.postAJobSaveDraft({
        job_id: decodeGraphqlNodeId(responseData.id).numericalId || "",
      })
      setIsCreatingDraft(false)
      onClose()
      onDone()
      toast.success("Job posting saved as draft")
    }
  }

  const updatePublishedJobPost = async (data: PostAJobModalFormValues) => {
    if (isSubmitting || step !== 3) return
    setIsSubmitting(true)

    const responseData: PostAJobResponseDataFragment | null = await upsertJobPosting(data)

    setResponseData(responseData)

    if (!responseData?.id) {
      setIsSubmitting(false)
      toast.error("There was an error updating this job")
      return
    } else {
      if (data.referenceImage?.file) {
        await uploadImageFile({
          file: data.referenceImage?.file,
          jobPostId: responseData?.id,
          fileName: data.referenceImage?.name,
        })
      }
      setIsSubmitting(false)
      onDone()
      onClose()
      toast.success("Job posting updated")
    }
  }

  return {
    step,
    isSubmitting,
    isCreatingDraft,
    onNext,
    onBack,
    onDone,
    createAndDraftJobPost,
    createAndPublishJobPost,
    wrapperRef,
    methods,
    handleReset,
    updatePublishedJobPost,
    stepOneDisplayFields,
    onChangeStepOneDisplayField,
    responseData,
    selectedCreativeIdsToInvite,
    handleClickInviteCard,
    handleInviteCreatives,
    isMutatingInviteCreatives,
    invitedCreatives,
  }
}
